import React from 'react';
import { motion } from 'framer-motion';

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  labelPosition?: 'left' | 'right';
  size?: 'sm' | 'md' | 'lg';
}

const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  label,
  labelPosition = 'right',
  size = 'md',
}) => {
  const handleToggle = () => {
    if (disabled) return;
    onChange(!checked);
  };

  // Size configurations
  const sizeClasses = {
    sm: {
      container: 'w-9 h-5',
      circle: 'w-4 h-4',
      translate: checked ? 'translate-x-4' : 'translate-x-0.5',
    },
    md: {
      container: 'w-11 h-6',
      circle: 'w-5 h-5',
      translate: checked ? 'translate-x-5' : 'translate-x-0.5',
    },
    lg: {
      container: 'w-14 h-7',
      circle: 'w-6 h-6',
      translate: checked ? 'translate-x-7' : 'translate-x-0.5',
    },
  };

  const currentSize = sizeClasses[size];

  return (
    <div className="flex items-center gap-3">
      {label && labelPosition === 'left' && (
        <span className="text-sm font-medium text-gray-700">{label}</span>
      )}
      
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          relative inline-flex items-center rounded-full transition-colors duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
          ${currentSize.container}
          ${checked ? 'bg-primary-600' : 'bg-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        aria-checked={checked}
        role="switch"
      >
        <motion.span
          initial={false}
          animate={{
            x: checked ? (size === 'sm' ? 16 : size === 'md' ? 20 : 28) : 2,
          }}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30,
          }}
          className={`
            ${currentSize.circle}
            inline-block rounded-full bg-white shadow-lg transform
          `}
        />
      </button>

      {label && labelPosition === 'right' && (
        <span className="text-sm font-medium text-gray-700">{label}</span>
      )}
    </div>
  );
};

export default Switch;